import { ViewableResponse } from '../types/global';
import { FieldHelper } from '@/components/PatientData/FieldHelper';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { useEffect, useRef, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';

// Cap the backing-store scale so six full pages stay light on phone memory.
const MAX_PIXEL_RATIO = 2;

const LOCKED_HINT =
  "Faites défiler le document jusqu'à la dernière page ou téléchargez-le pour pouvoir cocher la case";

// Legacy build: the modern one needs Uint8Array.prototype.toHex (Chrome 140+ /
// iOS Safari 18.2+), which many patients' phones don't have.
const loadPdfjs = async () => {
  const [pdfjs, { default: workerSrc }] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker&url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
  return pdfjs;
};

interface ResponseDocumentAcknowledgementFieldProps {
  response: ViewableResponse;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

export const ResponseDocumentAcknowledgementField = ({
  response,
  value,
  onChange,
  disabled,
}: ResponseDocumentAcknowledgementFieldProps) => {
  const documentUrl = response.documentUrl;
  const viewerRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );
  // An already-ticked box (back navigation) stays unlocked, and so does one
  // with no document to read, so a KB slip never blocks the questionnaire.
  const [unlocked, setUnlocked] = useState(Boolean(value) || !documentUrl);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const pagesContainer = pagesRef.current;
    if (!documentUrl || !pagesContainer) return;

    let cancelled = false;
    let observer: IntersectionObserver | undefined;
    let destroyDocument: (() => void) | undefined;

    (async () => {
      const pdfjs = await loadPdfjs();
      if (cancelled) return;
      const loadingTask = pdfjs.getDocument(documentUrl);
      destroyDocument = () => void loadingTask.destroy();
      const pdf = await loadingTask.promise;
      const width = pagesContainer.clientWidth;
      const pixelRatio = Math.min(
        window.devicePixelRatio || 1,
        MAX_PIXEL_RATIO,
      );

      const canvases: HTMLCanvasElement[] = [];
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({
          scale: (width / page.getViewport({ scale: 1 }).width) * pixelRatio,
        });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = 'block h-auto w-full bg-white shadow-sm';
        canvas.setAttribute('aria-label', `Page ${pageNumber}`);
        pagesContainer.appendChild(canvas);
        canvases.push(canvas);
        await page.render({ canvas, viewport }).promise;
      }
      if (cancelled) return;
      setStatus('ready');

      // "Reached the end" = the last page enters the viewer, not its bottom.
      const lastPage = canvases[canvases.length - 1];
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setUnlocked(true);
            observer?.disconnect();
          }
        },
        { root: viewerRef.current },
      );
      observer.observe(lastPage);
    })().catch(() => {
      if (!cancelled) setStatus('error');
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
      destroyDocument?.();
      pagesContainer.replaceChildren();
    };
  }, [documentUrl]);

  useEffect(() => {
    if (unlocked) setShowHint(false);
  }, [unlocked]);

  const onLabelClick = (event: React.MouseEvent) => {
    if (unlocked) return;
    // readOnly only stops the checkbox's own click; the label would still
    // forward it to the hidden input and tick the box.
    event.preventDefault();
    setShowHint(true);
  };

  return (
    <div className="flex w-full flex-col gap-3">
      {documentUrl && (
        <div
          ref={viewerRef}
          data-testid="document-viewer"
          className="relative h-[60vh] max-h-[640px] w-full overflow-y-auto rounded-1.5xl border border-gray-200 bg-gray-100 p-2 shadow-[0px_1.758px_7.031px_0px_rgba(0,0,0,0.06)]"
        >
          {status === 'loading' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner className="size-6 text-gray-400" />
            </div>
          )}
          {status === 'error' && (
            <p className="p-4 text-center text-sm text-gray-600">
              Le document ne peut pas être affiché ici. Téléchargez-le pour le
              lire.
            </p>
          )}
          <div ref={pagesRef} className="flex flex-col gap-2" />
        </div>
      )}

      {documentUrl && (
        <a
          href={documentUrl}
          download
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setUnlocked(true)}
          className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-indigo-700 underline underline-offset-2"
        >
          <MdOutlineFileDownload className="size-5" />
          Télécharger
        </a>
      )}

      <div className="flex flex-col gap-1">
        <label
          className="flex cursor-pointer items-start gap-3 text-sm text-gray-900"
          onClick={onLabelClick}
        >
          <Checkbox
            className="mt-0.5"
            checked={Boolean(value)}
            readOnly={!unlocked}
            disabled={disabled}
            onCheckedChange={(checked) => onChange(checked)}
          />
          <span>{response.label}</span>
        </label>
        {showHint && <FieldHelper error>{LOCKED_HINT}</FieldHelper>}
      </div>
    </div>
  );
};
